import { useEffect, useState } from 'react'
import Button from '@mui/joy/Button'
import CircularProgress from '@mui/joy/CircularProgress'
import Modal from '@mui/joy/Modal'
import ModalClose from '@mui/joy/ModalClose'
import ModalDialog from '@mui/joy/ModalDialog'
import Typography from '@mui/joy/Typography'
import { MobileLoadAccessHistoryEntry, MobileLoadAccessPolicy, mobileLoadAccessApi, mobileLoadAccessErrorMessage } from 'api/mobileLoadAccess'

interface Props {
    policy: MobileLoadAccessPolicy | null
    onClose: () => void
}

export default function MobileLoadAccessHistory({ policy, onClose }: Props) {
    const [items, setItems] = useState<MobileLoadAccessHistoryEntry[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (!policy) return
        setLoading(true)
        setError(null)
        void mobileLoadAccessApi
            .getPolicyHistory(policy._id)
            .then(setItems)
            .catch((reason) => setError(mobileLoadAccessErrorMessage(reason, 'Failed to load history')))
            .finally(() => setLoading(false))
    }, [policy])

    return (
        <Modal open={Boolean(policy)} onClose={onClose}>
            <ModalDialog sx={{ width: 'min(760px, calc(100vw - 32px))', maxHeight: '90vh', overflow: 'auto' }}>
                <ModalClose />
                <Typography level="h3">Rule history</Typography>
                <Typography level="body-sm" textColor="neutral.600">
                    MC {policy?.normalizedMc} · every change is applied immediately and retained here.
                </Typography>
                {loading ? (
                    <div className="flex justify-center py-10">
                        <CircularProgress />
                    </div>
                ) : error ? (
                    <Typography color="danger" sx={{ my: 3 }}>
                        {error}
                    </Typography>
                ) : (
                    <div className="mt-4 space-y-3">
                        {items.map((entry) => (
                            <article key={entry._id} className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <span className="font-semibold capitalize text-slate-900 dark:text-slate-100">{entry.action}</span>
                                    <time className="text-xs text-slate-500">{new Date(entry.createdAt).toLocaleString()}</time>
                                </div>
                                <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                                    {entry.operator} · revision {entry.after?.revision ?? entry.before?.revision ?? '—'}
                                </p>
                                <pre className="mt-3 max-h-48 overflow-auto rounded bg-slate-50 p-3 text-xs text-slate-700 dark:bg-slate-950 dark:text-slate-300">
                                    {JSON.stringify({ before: entry.before, after: entry.after }, null, 2)}
                                </pre>
                            </article>
                        ))}
                        {items.length === 0 && <Typography textColor="neutral.600">No history entries found.</Typography>}
                    </div>
                )}
                <div className="mt-4 flex justify-end">
                    <Button variant="plain" color="neutral" onClick={onClose}>
                        Close
                    </Button>
                </div>
            </ModalDialog>
        </Modal>
    )
}
